import { useEffect, useState, useCallback } from 'react';
import { Button, Flex, Radio, Spin, Alert, Tooltip } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import {
  CartesianGrid,
  Tooltip as ChartTooltip,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts';
import { createNotification } from '@/components';
import { useAuth } from '@/contexts/auth';
import {
  getTransactionSummary,
  sendTransactionSummary,
} from '@/services/billing/api';
import BillingAttemptsByRiskSection from './components/BillingAttemptsByRiskSection';
import BillingTable from './components/BillingTable';
import SelectMonthYear from './components/SelectMonthYear';

function Billing() {
  const intl = useIntl();
  const { currentWorkspace, partner } = useAuth();
  const { locale } = useSelector(state => state.LanguageSwitcher.language);

  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const formattedDate = `${year}-${month}`;

  const [billingType, setBillingType] = useState('workspace');
  const [dateFilter, setDateFilter] = useState(formattedDate);
  const [data, setData] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [summary, setSummary] = useState({
    total_count: 0,
    total_amount: 0,
    currency: 'USD',
    attempts: [],
  });
  const [loading, setLoading] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [error, setError] = useState(null);

  const width = 1400;
  const height = 350;
  const colors = ['#E16408', '#00ABAB', '#263238', '#788195'];

  const transformData = useCallback(
    (dailyData, currency = 'USD') => {
      const tableData = [];
      const chartData = [];

      Object.keys(dailyData).forEach(day => {
        const dayData = dailyData[day];

        tableData.push({
          date: dayData.date,
          total_count: dayData.total_count,
          total_amount: dayData.total_amount,
          transactions: dayData.transactions,
        });

        chartData.push({
          date: dayData.date || day,
          onboarding: dayData.transactions?.onboarding?.count || 0,
          verification: dayData.transactions?.verification?.count || 0,
          idCapture: dayData.transactions?.id_capture?.count || 0,
          restApi: dayData.transactions?.rest_api?.count || 0,
          totalQuantity: dayData.total_count,
          totalPrice: new Intl.NumberFormat(locale, {
            style: 'currency',
            currency: currency,
          }).format(dayData.total_amount || 0),
        });
      });

      return { tableData, chartData };
    },
    [locale],
  );

  const fetchBilling = useCallback(async () => {
    setLoading(true);
    setError(null);

    const query = {
      date: dateFilter,
    };

    if (billingType === 'workspace') {
      query.workspace_id = currentWorkspace;
    }

    await getTransactionSummary(query)
      .then(response => {
        setSummary(response.summary);
        const { tableData, chartData } = transformData(
          response.daily,
          response.summary.currency,
        );
        setData(tableData);
        setChartData(chartData);
      })
      .catch(() => {
        setError(
          intl.formatMessage({
            id: 'page.billing.fetch.error',
          }),
        );
      })
      .finally(() => {
        setLoading(false);
      });
  }, [billingType, dateFilter, currentWorkspace, intl, transformData]);

  useEffect(() => {
    fetchBilling();
  }, [fetchBilling]);

  async function sendReport() {
    setReportLoading(true);
    const query = {
      date: dateFilter,
    };

    if (billingType === 'workspace') {
      query.workspace_id = currentWorkspace;
    }

    await sendTransactionSummary(query)
      .then(() => {
        createNotification(
          'success',
          intl.formatMessage({ id: 'page.billing.report.success' }),
        );
      })
      .catch(() => {
        createNotification(
          'error',
          intl.formatMessage({ id: 'page.billing.report.error' }),
        );
      })
      .finally(() => setReportLoading(false));
  }

  return (
    <div className="flex flex-col gap-3">
      {loading ? (
        <div className="flex justify-center items-center col-span-2">
          <Spin size="large" />
        </div>
      ) : error ? (
        <div className="col-span-2">
          <Alert
            message="Erro"
            description={error}
            type="error"
            showIcon
          />
        </div>
      ) : (
        <>
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
            <div>
              <FormattedMessage id={'page.billing.usage'} />
              <br />
              <span className="text-primary text-2xl font-bold">
                {new Intl.NumberFormat(locale, {
                  style: 'currency',
                  currency: summary.currency,
                }).format(summary.total_amount || 0)}
              </span>
            </div>
            <div className="flex flex-col lg:flex-row lg:items-center gap-5">
              <Flex
                vertical
                gap="middle"
              >
                <Radio.Group
                  size="large"
                  value={billingType}
                  onChange={e => setBillingType(e.target.value)}
                  buttonStyle="solid"
                >
                  <Radio.Button
                    className="text-center"
                    value="workspace"
                  >
                    <FormattedMessage id={'page.billing.workspace'} />
                  </Radio.Button>
                  <Radio.Button
                    className="text-center"
                    value="partner"
                  >
                    <FormattedMessage id={'page.billing.global'} />
                  </Radio.Button>
                </Radio.Group>
              </Flex>
              <Flex gap="middle">
                <SelectMonthYear
                  language={locale}
                  value={dateFilter}
                  onChange={setDateFilter}
                  maxDate={partner.created}
                />
                <Tooltip
                  placement="bottom"
                  title={<FormattedMessage id={'page.billing.export'} />}
                >
                  <Button
                    loading={reportLoading}
                    size="large"
                    onClick={sendReport}
                  >
                    <UploadOutlined className="text-lg" />
                  </Button>
                </Tooltip>
              </Flex>
            </div>
          </div>
          <div className="w-full">
            <h3 className="text-gray-700">
              <FormattedMessage id={'page.billing.chart.title'} />
            </h3>
          </div>
          <div className="flex flex-col lg:flex-row mb-5 gap-5">
            <div
              className="border rounded-lg lg:w-1/2"
              style={{ height: '450px' }}
            >
              <ResponsiveContainer
                width="100%"
                height="100%"
                minWidth={400}
                minHeight={350}
              >
                <LineChart
                  width={width}
                  height={height}
                  data={chartData}
                  margin={{ top: 50, right: 20, left: -20, bottom: 5 }}
                >
                  <YAxis
                    stroke={colors[3]}
                    allowDecimals={false}
                  />
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    stroke={colors[3]}
                    padding={{ left: 40, right: 40 }}
                    tickFormatter={value => {
                      if (typeof value === 'string' && value.includes('-')) {
                        const [year, month, day] = value.split('-');
                        return `${day}/${month}`;
                      }
                      const date = new Date(value);
                      return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}`;
                    }}
                  />
                  <ChartTooltip
                    labelFormatter={value => {
                      if (typeof value === 'string' && value.includes('-')) {
                        const [year, month, day] = value.split('-');
                        return `${day}/${month}/${year}`;
                      }
                      const date = new Date(value);
                      return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
                    }}
                    formatter={(value, name) => {
                      return [value, name];
                    }}
                  />
                  <Legend
                    iconType="circle"
                    style={{ top: 50, marginLeft: 50 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="verification"
                    name="Verification"
                    stackId="quantity"
                    activeDot={{ r: 8 }}
                    fill={colors[1]}
                    stroke={colors[1]}
                    minPointSize={1}
                    isAnimationActive={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="onboarding"
                    name="Onboarding"
                    stackId="quantity"
                    activeDot={{ r: 8 }}
                    fill={colors[0]}
                    stroke={colors[0]}
                    minPointSize={1}
                    isAnimationActive={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="idCapture"
                    name="ID Capture"
                    stackId="quantity"
                    activeDot={{ r: 8 }}
                    fill={colors[2]}
                    stroke={colors[2]}
                    minPointSize={1}
                    isAnimationActive={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="restApi"
                    name="REST API"
                    stackId="quantity"
                    activeDot={{ r: 8 }}
                    fill="#7B1FA2"
                    stroke="#7B1FA2"
                    minPointSize={1}
                    isAnimationActive={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="border rounded-lg lg:w-1/2 lg:flex-shrink-0">
              <BillingTable
                data={data}
                locale={locale}
                currency={summary.currency}
              />
            </div>
          </div>
          <BillingAttemptsByRiskSection attempts={summary.attempts} />
        </>
      )}
    </div>
  );
}

export default Billing;
